import { useState, useCallback, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Bed, Bath, Ruler, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import { SpotlightIconPattern } from "./SpotlightIconPattern";

interface SpotlightHome {
  address: string;
  city: string;
  state: string;
  zip: string;
  price: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  image_url: string;
  listing_status: string | null;
  days_on_zillow: number | null;
  custom_agent_name: string | null;
  agent_photo_url: string | null;
  views_count: number;
}

interface SpotlightHomesCarouselProps {
  homes: SpotlightHome[];
  primaryColor?: string | null;
}

function formatPrice(price: number | null): string {
  if (!price) return "Price N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * 3D tilt-on-hover effect — Apple-style perspective card.
 */
function HoverCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`${className} transition-transform duration-500 ease-out hover:scale-[1.03]`}>
      {children}
    </div>
  );
}

export function SpotlightHomesCarousel({ homes, primaryColor }: SpotlightHomesCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.1);
  const { ref: scrollRef, progress } = useScrollProgress();

  const accentColor = primaryColor ?? "#3B82F6";
  const parallaxY = (progress - 0.5) * -20;

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    setCount(api.scrollSnapList().length);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on("select", onSelect);
    return () => { api.off("select", onSelect); };
  }, [api, onSelect]);

  if (homes.length === 0) return null;

  const sortedHomes = [...homes].sort((a, b) => (b.price ?? 0) - (a.price ?? 0));

  return (
    <section ref={scrollRef} className="relative w-full py-16 md:py-24 overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)" }}>
      <SpotlightIconPattern />
      <div
        ref={animRef}
        className="max-w-[100rem] mx-auto px-6 will-change-transform"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <p className="max-w-7xl mx-auto text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-12">
          Our Listings
        </p>

        <div
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="relative">
          {/* Left fade */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none bg-gradient-to-r from-white to-transparent" />
          {/* Right fade */}
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none bg-gradient-to-l from-[#f8fafc] to-transparent" />

          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: homes.length > 1,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {sortedHomes.map((home, index) => (
                  <CarouselItem key={index} className="pl-4 basis-[45%] md:basis-[38%] lg:basis-[32%] py-4">
                    <HoverCard className="relative aspect-[3/4] rounded-2xl overflow-hidden group cursor-pointer">
                      <div className="absolute inset-0">
                      <img
                        src={home.image_url}
                        alt={home.address}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />

                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />


                      {/* Content overlay at bottom */}
                      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                        <p className="text-3xl md:text-4xl font-bold text-white">
                          {formatPrice(home.price)}
                        </p>
                        <p className="text-white font-semibold mt-2 truncate">{home.address}</p>
                        <p className="text-white/60 text-sm">
                          {home.city}, {home.state} {home.zip}
                        </p>

                        <div className="flex items-center gap-4 mt-3 text-sm text-white/70">
                          {home.beds != null && (
                            <span className="flex items-center gap-1">
                              <Bed className="h-3.5 w-3.5" /> {home.beds} bd
                            </span>
                          )}
                          {home.baths != null && (
                            <span className="flex items-center gap-1">
                              <Bath className="h-3.5 w-3.5" /> {home.baths} ba
                            </span>
                          )}
                          {home.sqft != null && (
                            <span className="flex items-center gap-1">
                              <Ruler className="h-3.5 w-3.5" /> {home.sqft.toLocaleString()} sqft
                            </span>
                          )}
                        </div>

                        {home.custom_agent_name && (
                          <div className="flex items-center gap-2 mt-3">
                            {home.agent_photo_url && (
                              <img
                                src={home.agent_photo_url}
                                alt={home.custom_agent_name}
                                className="h-6 w-6 rounded-full object-cover shrink-0 ring-1 ring-white"
                              />
                            )}
                            <p className="text-xs text-white/60">
                              Listed by {home.custom_agent_name}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Total Views badge — bottom right */}
                      {home.views_count > 0 && (
                        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5">
                          <Eye className="h-3.5 w-3.5 text-white/80" />
                          <span className="text-xs font-semibold text-white">
                            {home.views_count.toLocaleString()}
                          </span>
                        </div>
                      )}
                      </div>
                    </HoverCard>
                  </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          </div>

          {/* Navigation controls */}
          {count > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => api?.scrollPrev()}
                className="w-10 h-10 rounded-full flex items-center justify-center border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-400 transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              <div className="flex gap-1.5">
                {Array.from({ length: count }).map((_, i) => (
                  <button
                    key={i}
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: i === current ? "1.5rem" : "0.5rem",
                      backgroundColor: i === current ? accentColor : "#cbd5e1",
                    }}
                    onClick={() => api?.scrollTo(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={() => api?.scrollNext()}
                className="w-10 h-10 rounded-full flex items-center justify-center border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-400 transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
